/**
 * Burnout Garage — small, progressive enhancements for content pages.
 * Loaded with `defer` only by sections that need it:
 *   <faq-filter>       category filter for the FAQ page
 *   <order-lookup>     order number + email lookup (signed-in customers)
 *   <tracking-form>    carrier tracking link from a URL template
 *   <share-link>       native share / copy link on articles
 *   <country-province> province select for customer address forms
 *   form[data-confirm] confirm before submitting (delete address)
 * Every component works (or degrades to a plain link/form) without JS.
 */
(() => {
  if (window.themePagesLoaded) return;
  window.themePagesLoaded = true;

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const announce = (el, message) => {
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = message;
    });
  };

  /* ---------- <faq-filter> ---------- */
  define(
    'faq-filter',
    class FaqFilter extends HTMLElement {
      connectedCallback() {
        this.buttons = [...this.querySelectorAll('[data-filter]')];
        this.groups = [...this.querySelectorAll('[data-group]')];
        this.status = this.querySelector('[data-filter-status]');
        this.buttons.forEach((button) => {
          button.hidden = false;
          button.addEventListener('click', () => this.select(button));
        });
        const nav = this.querySelector('[data-filter-nav]');
        if (nav) nav.hidden = false;
      }

      select(active) {
        const value = active.dataset.filter;
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button === active)));
        let shown = 0;
        this.groups.forEach((group) => {
          const match = value === 'all' || group.dataset.group === value;
          group.hidden = !match;
          if (match) shown += group.querySelectorAll('details').length;
        });
        const template = this.dataset.textCount || '';
        announce(this.status, template.replace('[count]', shown));
      }
    }
  );

  /* ---------- <order-lookup> ---------- */
  const normalizeOrder = (value) =>
    String(value || '')
      .replace(/[#\s]/g, '')
      .toUpperCase();

  define(
    'order-lookup',
    class OrderLookup extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.result = this.querySelector('[data-lookup-result]');
        this.signin = this.querySelector('[data-lookup-signin]');
        this.notFound = this.querySelector('[data-lookup-not-found]');
        this.timeline = document.getElementById(this.dataset.timeline || '');
        const data = this.querySelector('[data-orders]');
        try {
          this.orders = data ? JSON.parse(data.textContent) : null;
        } catch (error) {
          this.orders = null;
        }
        if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      hideAll() {
        [this.result, this.signin, this.notFound].forEach((el) => {
          if (el) el.hidden = true;
        });
      }

      show(el) {
        if (!el) return;
        el.hidden = false;
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: 'nearest', behavior: window.theme && window.theme.motionOff && window.theme.motionOff() ? 'auto' : 'smooth' });
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.form.reportValidity()) return;
        const number = normalizeOrder(this.form.elements.order.value);
        const email = String(this.form.elements.email.value || '').trim().toLowerCase();
        this.hideAll();

        // Not signed in: we cannot (and should not) show order data publicly.
        if (!this.orders) {
          this.fillContact(number, email);
          this.show(this.signin);
          return;
        }

        const owner = String(this.dataset.customerEmail || '').toLowerCase();
        const order = this.orders.find(
          (item) => normalizeOrder(item.name) === number || normalizeOrder(item.number) === number
        );
        if (!order || (email && owner && email !== owner)) {
          this.fillContact(number, email);
          this.show(this.notFound);
          return;
        }
        this.render(order);
        this.show(this.result);
      }

      fillContact(number, email) {
        this.querySelectorAll('[data-contact-link]').forEach((link) => {
          const base = link.dataset.base || link.getAttribute('href');
          if (!link.dataset.base) link.dataset.base = base;
          if (!base.startsWith('mailto:')) return;
          const subject = `${this.dataset.textSubject || 'Order'} #${number}`.trim();
          const body = `${this.dataset.textOrder || 'Order'}: #${number}\n${this.dataset.textEmail || 'Email'}: ${email}\n\n`;
          link.href = `${base}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        });
      }

      render(order) {
        const set = (name, value) => {
          this.result.querySelectorAll(`[data-field="${name}"]`).forEach((el) => {
            el.textContent = value || '';
          });
        };
        set('name', order.name);
        set('date', order.date);
        set('status', order.status_label);

        const link = this.result.querySelector('[data-field="url"]');
        if (link) link.href = order.url;
        const status = this.result.querySelector('[data-field="status_url"]');
        if (status) {
          status.hidden = !order.status_url;
          if (order.status_url) status.href = order.status_url;
        }

        const list = this.result.querySelector('[data-field="tracking"]');
        if (list) {
          list.replaceChildren();
          (order.tracking || []).forEach((item) => {
            const li = document.createElement('li');
            const text = [item.company, item.number].filter(Boolean).join(' · ');
            if (item.url) {
              const a = document.createElement('a');
              a.href = item.url;
              a.target = '_blank';
              a.rel = 'noopener';
              a.className = 'chip';
              a.textContent = text || this.dataset.textTrack || 'Track';
              li.append(a);
            } else {
              li.textContent = text;
            }
            list.append(li);
          });
          list.closest('[data-tracking-wrap]')?.toggleAttribute('hidden', !(order.tracking || []).length);
        }

        if (this.timeline) {
          const stage = order.cancelled ? -1 : Number(order.stage) || 0;
          this.timeline.querySelectorAll('[data-stage]').forEach((step) => {
            const index = Number(step.dataset.stage);
            step.classList.toggle('is-done', stage >= 0 && index < stage);
            step.classList.toggle('is-current', stage >= 0 && index === stage);
            if (index === stage) step.setAttribute('aria-current', 'step');
            else step.removeAttribute('aria-current');
          });
        }
      }
    }
  );

  /* ---------- <tracking-form> ---------- */
  define(
    'tracking-form',
    class TrackingForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.error = this.querySelector('[data-tracking-error]');
        if (!this.form) return;
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          if (!this.form.reportValidity()) return;
          const number = String(this.form.elements.tracking_number.value || '').replace(/\s+/g, '');
          const select = this.form.elements.carrier;
          const template = select ? select.value : this.dataset.template;
          if (!template || !template.includes('{number}')) {
            if (this.error) this.error.hidden = false;
            return;
          }
          if (this.error) this.error.hidden = true;
          const url = template.replace('{number}', encodeURIComponent(number));
          const win = window.open(url, '_blank', 'noopener');
          if (!win) window.location.href = url;
        });
      }
    }
  );

  /* ---------- <share-link> ---------- */
  define(
    'share-link',
    class ShareLink extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.status = this.querySelector('[data-share-status]');
        if (!this.button) return;
        this.button.hidden = false;
        this.button.addEventListener('click', () => this.share());
      }

      async share() {
        const url = this.dataset.url || window.location.href;
        const title = this.dataset.title || document.title;
        try {
          if (navigator.share && matchMedia('(pointer: coarse)').matches) {
            await navigator.share({ title, url });
            return;
          }
          await navigator.clipboard.writeText(url);
          announce(this.status, this.dataset.textCopied || 'Link copied');
          this.button.dataset.state = 'copied';
          clearTimeout(this.timer);
          this.timer = setTimeout(() => delete this.button.dataset.state, 2400);
        } catch (error) {
          /* Share sheet dismissed or clipboard blocked: nothing to do. */
        }
      }
    }
  );

  /* ---------- <country-province> ---------- */
  define(
    'country-province',
    class CountryProvince extends HTMLElement {
      connectedCallback() {
        this.country = this.querySelector('[data-country]');
        this.province = this.querySelector('[data-province]');
        this.provinceWrap = this.querySelector('[data-province-wrap]');
        if (!this.country || !this.province) return;
        const initialCountry = this.country.dataset.default;
        if (initialCountry) {
          const option = [...this.country.options].find(
            (item) => item.value === initialCountry || item.textContent.trim() === initialCountry
          );
          if (option) this.country.value = option.value;
        }
        this.update(this.province.dataset.default);
        this.country.addEventListener('change', () => this.update());
      }

      update(selected) {
        const option = this.country.options[this.country.selectedIndex];
        let provinces = [];
        try {
          provinces = option ? JSON.parse(option.dataset.provinces || '[]') : [];
        } catch (error) {
          provinces = [];
        }
        this.province.replaceChildren();
        provinces.forEach(([value, label]) => {
          const item = new Option(label, value);
          if (selected && (selected === value || selected === label)) item.selected = true;
          this.province.add(item);
        });
        if (this.provinceWrap) this.provinceWrap.hidden = provinces.length === 0;
      }
    }
  );

  /* ---------- form[data-confirm] ---------- */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.dataset.confirm && !window.confirm(form.dataset.confirm)) {
      event.preventDefault();
    }
  });
})();
